import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit as qlimit,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import {
  FINANCIAL_KINDS,
  FINANCIAL_KIND_LABEL,
  type FinancialKind,
  type FinancialRecord,
} from '@sfp/shared/financial';

/**
 * Financials ledger. Reviews/edits the money facts the LLM extracted from
 * investment documents (commitments, capital calls, distributions, carried
 * interest, gifts, loans, valuations). The spine of the SFP accounting layer.
 */
export function Financials() {
  const { role } = useAuth();
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [kindFilter, setKindFilter] = useState<FinancialKind | 'all'>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'financialRecords'), orderBy('createdAt', 'desc'), qlimit(2000)),
      (snap) => setRecords(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<FinancialRecord, 'id'>) }))),
      () => setRecords([]),
    );
  }, []);

  const entities = useMemo(
    () => Array.from(new Set(records.map((r) => r.entity).filter(Boolean) as string[])).sort(),
    [records],
  );

  const rows = useMemo(
    () =>
      records.filter((r) => {
        if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
        if (entityFilter !== 'all' && (r.entity ?? '') !== entityFilter) return false;
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        return true;
      }),
    [records, kindFilter, entityFilter, statusFilter],
  );

  // Totals per kind (confirmed + extracted + edited, excluding rejected).
  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const r of rows) {
      if (r.status === 'rejected') continue;
      if (typeof r.amountCents === 'number') t[r.kind] = (t[r.kind] ?? 0) + r.amountCents;
    }
    return t;
  }, [rows]);

  if (role !== 'admin') {
    return <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>;
  }

  async function patch(id: string, fields: Partial<FinancialRecord>) {
    await updateDoc(doc(db, 'financialRecords', id), {
      ...fields,
      status: fields.status ?? 'edited',
      updatedAt: serverTimestamp(),
    });
  }

  async function addManual() {
    await addDoc(collection(db, 'financialRecords'), {
      kind: 'commitment',
      amountCents: null,
      currency: 'USD',
      status: 'confirmed',
      source: 'manual',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async function backfill() {
    if (!confirm('Re-run the LLM financial extraction across all investment documents? Uses your configured LLM (cost applies).')) return;
    setBusy(true);
    setMsg('Extracting… this can take a minute.');
    try {
      const fn = httpsCallable<unknown, { processed: number; totalFacts: number }>(functions, 'reextractAllFinancials');
      const r = await fn({});
      setMsg(`Processed ${r.data.processed} doc(s), extracted ${r.data.totalFacts} facts.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Accounting</div>
          <h1 className="text-2xl font-semibold text-stone-800">Financials</h1>
          <p className="text-sm text-stone-500">
            Money facts pulled from your investment documents — review, edit, and confirm. New uploads
            classified as investments / fund admin auto-populate here.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={addManual} className="rounded border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50">
            + Add line
          </button>
          <button type="button" disabled={busy} onClick={backfill} className="rounded bg-[color:var(--sfp-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {busy ? 'Extracting…' : 'Extract from all docs'}
          </button>
        </div>
      </header>

      {msg && <div className="rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700">{msg}</div>}

      {/* Summary cards by kind */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(['commitment', 'capital_call', 'distribution', 'carried_interest', 'gift', 'loan', 'valuation', 'capital_account'] as FinancialKind[])
          .filter((k) => totals[k])
          .map((k) => (
            <div key={k} className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-stone-400">{FINANCIAL_KIND_LABEL[k]}</div>
              <div className="mt-0.5 text-lg font-semibold text-stone-800">{fmtUsd(totals[k]!)}</div>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as FinancialKind | 'all')} className="h-8 rounded border border-stone-300 px-2">
          <option value="all">All kinds</option>
          {FINANCIAL_KINDS.map((k) => <option key={k} value={k}>{FINANCIAL_KIND_LABEL[k]}</option>)}
        </select>
        <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="h-8 rounded border border-stone-300 px-2">
          <option value="all">All entities</option>
          {entities.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 rounded border border-stone-300 px-2">
          <option value="all">All statuses</option>
          {['extracted', 'confirmed', 'edited', 'rejected'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-stone-400">{rows.length} line(s)</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm">
        <table className="w-full text-xs">
          <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Amount (USD)</th>
              <th className="px-3 py-2">%</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Fund / Counterparty</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-stone-400">
                No financial records yet. Upload investment docs, or click “Extract from all docs”.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-100 align-top last:border-b-0">
                <td className="px-3 py-2">
                  <select value={r.kind} onChange={(e) => patch(r.id, { kind: e.target.value as FinancialKind })} className="rounded border border-stone-300 px-1 py-0.5">
                    {FINANCIAL_KINDS.map((k) => <option key={k} value={k}>{FINANCIAL_KIND_LABEL[k]}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    defaultValue={r.amountCents != null ? (r.amountCents / 100).toString() : ''}
                    onBlur={(e) => {
                      const v = e.target.value.replace(/[^0-9.-]/g, '');
                      const cents = v ? Math.round(parseFloat(v) * 100) : null;
                      if (cents !== r.amountCents) patch(r.id, { amountCents: Number.isFinite(cents as number) ? cents : null });
                    }}
                    placeholder="—"
                    className="w-28 rounded border border-stone-300 px-1.5 py-0.5 text-right font-mono"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    defaultValue={r.percentage != null ? String(r.percentage) : ''}
                    onBlur={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, '');
                      patch(r.id, { percentage: v ? parseFloat(v) : null });
                    }}
                    placeholder="—"
                    className="w-12 rounded border border-stone-300 px-1 py-0.5 text-right font-mono"
                  />
                </td>
                <td className="px-3 py-2">
                  <input type="date" defaultValue={(r.date ?? '').slice(0, 10)} onBlur={(e) => { if (e.target.value !== (r.date ?? '').slice(0,10)) patch(r.id, { date: e.target.value }); }} className="rounded border border-stone-300 px-1 py-0.5" />
                </td>
                <td className="px-3 py-2">
                  <input defaultValue={r.entity ?? ''} onBlur={(e) => { if (e.target.value !== (r.entity ?? '')) patch(r.id, { entity: e.target.value }); }} className="w-32 rounded border border-stone-300 px-1.5 py-0.5" placeholder="entity" />
                </td>
                <td className="px-3 py-2">
                  <input defaultValue={r.fund ?? ''} onBlur={(e) => { if (e.target.value !== (r.fund ?? '')) patch(r.id, { fund: e.target.value }); }} className="mb-1 w-40 rounded border border-stone-300 px-1.5 py-0.5" placeholder="fund" />
                  <input defaultValue={r.counterparty ?? ''} onBlur={(e) => { if (e.target.value !== (r.counterparty ?? '')) patch(r.id, { counterparty: e.target.value }); }} className="w-40 rounded border border-stone-300 px-1.5 py-0.5" placeholder="counterparty" />
                </td>
                <td className="px-3 py-2">
                  {r.docId ? (
                    <Link to={`/documents/${r.docId}`} className="text-[color:var(--sfp-primary)] hover:underline" title={r.docName ?? ''}>
                      {(r.docName ?? 'source').slice(0, 24)}
                    </Link>
                  ) : (
                    <span className="text-stone-400">manual</span>
                  )}
                  {r.description && <div className="mt-0.5 text-[10px] text-stone-400">{r.description}</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  <select
                    value={r.status}
                    onChange={(e) => patch(r.id, { status: e.target.value as FinancialRecord['status'] })}
                    className={`rounded px-1 py-0.5 ${r.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : r.status === 'rejected' ? 'bg-stone-100 text-stone-500' : r.status === 'edited' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}
                  >
                    {['extracted', 'confirmed', 'edited', 'rejected'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => { if (confirm('Delete this line?')) void deleteDoc(doc(db, 'financialRecords', r.id)); }} className="ml-2 text-[#9C4A3A] hover:underline">del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
